import 'react-rangeslider/lib/index.css';
import './breadcrumbsContainer.scss';
import { Loading, toast } from '@components';
import { Api } from '@services';
import moment from 'moment';
import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import { Button } from 'react-bootstrap';
import Slider from 'react-rangeslider';
import { handleTimeoutApiError } from '../../../utils/handleTimeoutApiError';
import BreadcrumbsTable from '../components/breadcrumbsTable';
import LocationMap from '../components/locationMap';
import PlaybackControls from '../components/playbackControls';
import { Tooltip as ReactTooltip } from 'react-tooltip';

async function getGeoTimeSeries(thingId, startDate, endDate, limit, sortOrder, skip, page) {
  const url = `/assets/${thingId}/breadcrumbs?start=${moment(startDate).toISOString()}&end=${moment(
    endDate,
  ).toISOString()}&limit=${limit}&page=${page}`;

  return Api.get(url);
}

function mapGateways(gateways) {
  return gateways.map((g) => ({
    ...g,
    location: { ...g.location, type: 0 },
    gateway: { ...g },
  }));
}

function mapBeacons(beacons) {
  return beacons.map((b) => ({
    ...b,
    location: { ...b.location, type: 0 },
    beacon: { ...b },
  }));
}

const BreadcrumbsContainer = ({ match }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [maps, setMaps] = useState([]);
  const [locations, setLocations] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [pageCount, setPageCount] = useState('0');
  const [thing, setThing] = useState({});
  const [query, setQuery] = useState({
    limit: 50,
    page: '1',
    sortOrder: 1,
    sortField: 'timestamp',
    filter: '',
    startDate: undefined,
    endDate: undefined,
  });
  const [mapDisplayType, setMapDisplayType] = useState('lines');
  const [playback, setPlayback] = useState(false);
  const [showPlaybackControls, setShowPlaybackControls] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState('Normal');
  const [visibility, setVisibility] = useState({
    gateways: false,
    zones: false,
    beacons: false,
    outliers: false,
  });
  const [currentlySelected, setCurrentlySelected] = useState(null);
  const [pace, setPace] = useState(750);
  const [paused, setPaused] = useState(false);
  const [ready, setReady] = useState(false);
  const [fallbackMap, setFallbackMap] = useState(null);
  const [units, setUnits] = useState(null);
  const [isError, setIsError] = useState(false);
  const [lastLocationReading, setLastLocationReading] = useState(null);
  const [sortedLocations, setSortedLocations] = useState([]);
  const [mapIdToName, setMapIdToName] = useState({});
  const [hasMore, setHasMore] = useState(true);

  const PAGE_SIZE = 50; // how many to fetch at a time
  const [limit, setLimit] = useState(PAGE_SIZE);

  // Button Handlers for play, pause, forward, ect

  const playTimeoutRef = useRef(null);

  // Sync playback state in a ref so setTimeout reads latest value
  const playbackRef = useRef(playback);
  useEffect(() => {
    playbackRef.current = playback;
  }, [playback]);

  const play = (startIndex = 0) => {
    let i = startIndex;

    if (!locations.length) return;

    if (currentlySelected === null || currentlySelected === undefined) {
      changeSelection(0);
      setCurrentlySelected(0);
      i = 0;
    }

    const drawMarkerAtPace = () => {
      if (!playbackRef.current) return;
      playTimeoutRef.current = setTimeout(() => {
        if (!locations[i]) return;

        changeSelection(i);
        setCurrentlySelected(i);

        i++;
        if (i < locations.length) {
          drawMarkerAtPace();
        }
      }, pace);
    };

    drawMarkerAtPace();
  };

  const handlePlayback = () => {
    if (paused && currentlySelected !== null) {
      setPaused(false);
      setPlayback(true);
      play(currentlySelected);
      return;
    }

    const shouldPlay = !playback;
    setPaused(false);
    setPlayback(shouldPlay);
    setCurrentlySelected(null);

    if (shouldPlay) {
      setTimeout(() => {
        play();
      }, 0);
    }
  };

  const handlePause = (pausedNum) => {
    clearTimeout(playTimeoutRef.current);
    setPlayback(true);
    setPaused(true);
    setCurrentlySelected(pausedNum);
    changeSelection(pausedNum);
  };

  const resetToDefault = () => {
    clearTimeout(playTimeoutRef.current);
    setPlayback(false);
    setPaused(false);
    setCurrentlySelected(0);
    changeSelection(0);
  };

  const frameChange = (direction) => {
    handlePause();

    const index = currentlySelected ?? 0;
    const step = direction === 'up' ? 1 : -1;
    const newIndex = (index + step + locations.length) % locations.length;

    changeSelection(newIndex);
  };

  const showPlaybackControl = () => {
    setShowPlaybackControls(!showPlaybackControls);
  };

  const handlePlaybackSpeed = (selection) => {
    setPlaybackSpeed(selection);
    const rate = selection === 'Normal' ? 1 : selection;
    setPace(750 / rate);
  };

  const getThingDetails = async (thingId) => {
    try {
      // fetch asset info (for name, type, etc.)
      const { data: asset } = await Api.get(`/v2/assets/${thingId}`);
      const { data: lastLocation } = await Api.get(`/v2/assets/${thingId}/lastLocation`);

      // normalize lastLocation (array or object)
      const loc = Array.isArray(lastLocation) ? lastLocation[0] : lastLocation;
      if (!loc || !loc.mapId || !loc.coordinates) {
        console.warn('⚠️ No valid lastLocation data for this asset.');
        setThing(asset);
        return;
      }

      // unwrap coordinates
      const coords = (loc.coordinates && loc.coordinates.coordinates) || loc.coordinates || null;

      asset.lastLocationCoordinates = { ...coords, type: 0 };
      setLastLocationReading(asset.lastLocationReading);

      // fetch map image for fallback
      const imageBlobResponse = await Api.get(`/v2/maps/${loc.mapId}/image`, {
        responseType: 'blob',
      });
      const imageUrl = URL.createObjectURL(imageBlobResponse.data);

      const fallbackMap = {
        id: loc.mapId,
        imageUrl,
        gateways: [],
        zones: [],
        outliers: [],
      };

      setThing(asset);
      setFallbackMap(fallbackMap);
    } catch (err) {
      console.error('Error loading thing or map image:', err);
    }
  };

  // Getting correct units for movement
  const getUnitDetails = async () => {
    try {
      const { data } = await Api.get(`/users`);
      const unit = data.distanceUnit === 'Imperial' ? 'ft' : 'm';
      setUnits(unit);
    } catch (error) {
      setIsError(true);
    }
  };

  useEffect(() => {
    const thingId = match.params.thingId;
    getThingDetails(thingId);
    getUnitDetails();
  }, []);

  useEffect(() => {
    if (locations.length > 0 && currentlySelected === null) {
      changeSelection(0);
    }
  }, [locations]);

  const getTimeSeries = (sortOrder, startDate, endDate, overrideLimit, page) => {
    const { thingId } = match.params;
    const effectiveLimit = overrideLimit ?? limit;
    const skip = (page - 1) * effectiveLimit;

    setIsLoading(true);

    getGeoTimeSeries(thingId, startDate, endDate, effectiveLimit, sortOrder, skip, page)
      .then((x) => {
        const { results, total } = x.data.locations;

        if (!results || results.length === 0) {
          setHasMore(false);
          return;
        }

        const processedLocations = results.map((r, i) => ({
          ...r,
          index: skip + i,
          isVisible: true,
          isHighlighted: false,
        }));

        setLocations((prev) => {
          const updatedLocations = [...prev, ...processedLocations];

          setSortedLocations(sortLocationData('full', updatedLocations));

          return updatedLocations;
        });

        setTotalItems(total);
        setMaps((prevMaps) => {
          const incomingMaps = x.data.maps || [];
          const mergedMaps = [...prevMaps];

          incomingMaps.forEach((incomingMap) => {
            const existingIndex = mergedMaps.findIndex((m) => m.id === incomingMap.id);

            if (existingIndex === -1) {
              mergedMaps.push(incomingMap);
            } else {
              mergedMaps[existingIndex] = {
                ...mergedMaps[existingIndex],
                ...incomingMap,
              };
            }
          });

          return mergedMaps;
        });

        setMapIdToName((prev) => ({
          ...prev,
          ...Object.fromEntries((x.data.maps || []).map(({ id, name }) => [id, name])),
        }));
        setReady(true);
      })
      .catch((error) => {
        console.error('Error fetching time series data:', error);
        handleTimeoutApiError(
          error,
          'Your request timed out. Please select fewer data and try again.',
        );
      })
      .finally(() => setIsLoading(false));
  };
  const sortLocationData = (type = 'full', locationsInput) => {
    const locs = locationsInput || locations;

    if (!locs || locs.length === 0) return;

    const datasets = [];
    const sliderLabelData = {};

    let index = 0;
    let prevMapId = locs[0].mapId;

    locs.forEach((value, i) => {
      if (value.mapId === prevMapId) {
        datasets[index] = datasets[index] || [];
        datasets[index].push(value);
        sliderLabelData[index] = '|';
      } else {
        index = value.index;
        datasets[index] = [value];
        sliderLabelData[index] = '|';
      }

      prevMapId = value.mapId;
    });

    return type === 'full' ? datasets : sliderLabelData;
  };

  const renderSliderLabels = () => {
    if (maps.length > 1) {
      return sortLocationData('slider');
    } else {
      return {};
    }
  };

  const changeMapDisplayType = (value) => {
    setMapDisplayType(value);
  };

  const toggleVisibility = (name) => {
    const map =
      (isReadyWithLocations() && getCurrentLocation().location && getCurrentMap()) || fallbackMap;

    if (!map) {
      toast.error('Failed to find a Map.');
      return;
    }

    const updatedMaps = [...maps];
    const visibilityCopy = { ...visibility };
    const newVisibility = !visibilityCopy[name];

    if (name === 'zones') {
      map.zones = (map.zones || []).map((zone) => ({
        ...zone,
        isVisible: newVisibility,
      }));
    }

    if (map[name]) {
      map[name] = map[name].map((item) => ({
        ...item,
        isVisible: newVisibility,
      }));
    }

    const index = updatedMaps.findIndex((val) => val.id === map.id);
    if (index !== -1) {
      updatedMaps[index] = map;
    }

    visibilityCopy[name] = newVisibility;

    setMaps(updatedMaps);
    setVisibility(visibilityCopy);
  };

  const queryChange = (page, limitArg, sortOrder, sortField, startDate, endDate) => {
    if (!startDate || !endDate) return;

    // Stop playback so we don't increment indexes into the void
    resetToDefault();

    const newQuery = {
      page,
      limit: PAGE_SIZE,
      sortOrder,
      sortField,
      startDate,
      endDate,
    };

    setQuery(newQuery);

    if (String(page) === '1') {
      setLocations([]);
      setMaps([]);
      setMapIdToName({});
      setHasMore(true);
    }

    getTimeSeries(sortOrder, startDate, endDate, PAGE_SIZE, page);
  };

  const changeSelection = (index) => {
    const updatedLocations = locations.map((l) => {
      if (l.index === index) {
        return {
          ...l,
          isHighlighted: l.index === index,
          isVisible: true,
        };
      } else {
        return {
          ...l,
          isHighlighted: false,
        };
      }
    });

    setLocations(updatedLocations);
    setCurrentlySelected(index);
  };

  const handleSliderChange = (value) => {
    handlePause(value);
  };

  const renderFallback = () => {
    if (!fallbackMap) {
      return <Loading />;
    }

    return (
      <div className="card">
        <LocationMap
          map={fallbackMap}
          marker={thing.lastLocationCoordinates}
          lastLocationReading={lastLocationReading}
        />
      </div>
    );
  };

  /**
   * determines if component is ready and has locations data to show
   */

  const isReadyWithLocations = () => {
    return ready && currentlySelected !== null && locations?.length > 0;
  };

  const getCurrentMap = () => {
    const currentMapId = locations?.[currentlySelected]?.mapId;
    if (!currentMapId) return null;

    return maps.find((map) => map.id === currentMapId) || null;
  };

  const getCurrentLocation = () => {
    return locations[currentlySelected];
  };

  const renderLocationMap = () => {
    const map = isReadyWithLocations() && getCurrentLocation().location && getCurrentMap();

    if (!map) {
      return (
        <div>
          <h1>No map was found.</h1>
        </div>
      );
    }

    return (
      <div className="card">
        <LocationMap
          pace={pace}
          map={map}
          locations={locations}
          sortedLocations={sortedLocations}
          gateways={mapGateways(map.gateways)}
          zones={map.zones}
          beacons={mapBeacons(map.beacons)}
          marker={getCurrentLocation().location}
          playback={playback}
          displayType={mapDisplayType}
          selectedIndex={currentlySelected}
          paused={paused}
        />
      </div>
    );
  };

  const renderMap = () => {
    if (!isReadyWithLocations()) return renderFallback();

    return renderLocationMap();
  };

  const ControlButton = ({ iconClass, title, onClick, variant = 'secondary', children }) => (
    <Button
      type="button"
      variant={variant}
      onClick={onClick}
      title={title}
      data-tooltip-id="tooltip"
      data-tooltip-content={title}
    >
      {iconClass && <i className={iconClass} />}
      {children}
    </Button>
  );

  const playbackButtons = [
    {
      icon: 'fas fa-undo-alt',
      title: 'Restart sequence',
      onClick: resetToDefault,
    },
    {
      icon: 'fas fa-step-backward',
      title: 'Step backward',
      onClick: (e) => {
        e.preventDefault();
        frameChange('down');
      },
      variant: 'secondary',
    },
    {
      condition: playback === false || paused === true,
      icon: 'fa fa-play',
      title: 'Play sequence',
      onClick: handlePlayback,
      variant: playback && !paused ? 'primary' : 'secondary',
    },
    {
      condition: playback === true && paused === false,
      icon: 'fa fa-pause',
      title: 'Pause sequence',
      onClick: (e) => {
        e.preventDefault();
        handlePause(currentlySelected);
      },
      variant: paused ? 'primary' : 'secondary',
    },
    {
      icon: 'fas fa-step-forward',
      title: 'Step forward',
      onClick: (e) => {
        e.preventDefault();
        frameChange('up');
      },
      variant: 'secondary',
    },
  ];

  const renderControls = () => {
    return (
      <div className="d-flex app-toolbar align-items-center justify-content-between mb-2 mt-3">
        <div className="btn-group restart-sequence-btn">
          {playbackButtons.map(
            (btn, i) =>
              btn.condition !== false && (
                <ControlButton
                  key={i}
                  iconClass={btn.icon}
                  title={btn.title}
                  onClick={btn.onClick}
                  variant={btn.variant}
                />
              ),
          )}
          <Button
            className="playback-speed"
            type="button"
            variant="secondary"
            onClick={(e) => {
              e.preventDefault();
              showPlaybackControl();
            }}
            data-tooltip-id="tooltip"
            data-tooltip-content="Playback speed"
          >
            <i className="fas fa-tachometer-alt" />
            <PlaybackControls
              hidden={!showPlaybackControls}
              playbackSpeed={playbackSpeed}
              handlePlaybackSpeed={handlePlaybackSpeed}
              items={['0.25', '0.5', '0.75', 'Normal', '1.25', '1.5', '1.75', '2']}
            />
          </Button>
        </div>
        <div className="slider-container">
          <Slider
            min={0}
            max={locations.length ? locations.length - 1 : 0}
            value={currentlySelected || 0}
            orientation="horizontal"
            onChange={handleSliderChange}
            labels={renderSliderLabels()}
          />
        </div>
        <div className="btn-group mb-3">
          <Button
            variant={mapDisplayType === 'points' ? 'primary' : 'secondary'}
            onClick={() => changeMapDisplayType('points')}
            data-tooltip-id="tooltip"
            data-tooltip-content="Show Points"
          >
            <i className="fa fa-chart-scatter" />
          </Button>
          <Button
            variant={mapDisplayType === 'lines' ? 'primary' : 'secondary'}
            onClick={() => changeMapDisplayType('lines')}
            data-tooltip-id="tooltip"
            data-tooltip-content="Show Path"
          >
            <i className="fa fa-chart-line-down" />
          </Button>
          <Button
            variant={visibility.gateways ? 'primary' : 'secondary'}
            onClick={() => toggleVisibility('gateways')}
            data-tooltip-id="tooltip"
            data-tooltip-content="Toggle Gateways"
          >
            <i className="sonarIcon sonarIcon-gateway" />
          </Button>
          <Button
            variant={visibility.zones ? 'primary' : 'secondary'}
            onClick={() => toggleVisibility('zones')}
            data-tooltip-id="tooltip"
            data-tooltip-content="Toggle Zones"
          >
            <i className="sonarIcon sonarIcon-place" />
          </Button>
          <Button
            variant={visibility.beacons ? 'primary' : 'secondary'}
            onClick={() => toggleVisibility('beacons')}
            data-tooltip-id="tooltip"
            data-tooltip-content="Toggle Beacons"
          >
            <i className="fa fa-tags" />
          </Button>
          <Button
            variant={visibility.outliers ? 'primary' : 'secondary'}
            onClick={() => toggleVisibility('outliers')}
            data-tooltip-id="tooltip"
            data-tooltip-content="Toggle Outliers"
          >
            <i className="fas fa-bullseye" />
          </Button>
        </div>
        <ReactTooltip id="tooltip" place="top" delayShow={300} />
      </div>
    );
  };

  const currentMapId = locations?.[currentlySelected]?.mapId;
  const mapName = mapIdToName?.[currentMapId] || thing?.location?.mapName || 'Loading...';

  return (
    <div className="bread-crumbs-table-container">
      <div className="ps-5 pe-5 pb-5">
        <div className="d-flex app-toolbar align-items-center justify-content-between mb-3 mt-3">
          <h4 className="ms-4 mb-0 cg-text">
            <i className="fa fa-tags" /> {thing?.name || 'Loading...'}
          </h4>

          <h4 className="ms-4 mb-0 cg-text">
            <i className="fa fa-map" /> {mapName}
          </h4>
        </div>

        <div className="card">
          <BreadcrumbsTable
            allowFilter={true}
            locations={locations.length ? locations : []}
            hasMore={hasMore}
            totalItems={Number(totalItems)}
            pageCount={pageCount}
            sortField={query.sortField}
            sortOrder={query.sortOrder}
            limit={query.limit}
            page={String(query.page)}
            startDate={query.startDate}
            endDate={query.endDate}
            onQueryChanged={queryChange}
            onRowSelect={changeSelection}
            current={currentlySelected}
            units={units}
            isLoading={isLoading}
          />
        </div>

        {renderControls()}

        {renderMap()}
      </div>
    </div>
  );
};

BreadcrumbsContainer.propTypes = {
  map: PropTypes.object,
  locations: PropTypes.array,
  lastLocation: PropTypes.object,
  match: PropTypes.object,
};
export default BreadcrumbsContainer;
